import React, { useEffect, useMemo, useState } from 'react'
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, MenuItem,
  Grid, Typography, Divider, Chip, InputAdornment,
} from '@material-ui/core'
import { Lock } from '@material-ui/icons'
import { NumericFormat } from 'react-number-format'
import { useTranslation } from 'react-i18next'
import UploadFile from 'components/UploadFile/UploadFile'
import {
  criarTitulo, editarTitulo, getSaldoCertificado, getEmpresas, getCertificadoAtivo,
} from 'store/modules/api/titulos-lbc/titulos-lbc.api'
import { getProjetosElegiveis } from 'store/modules/api/titulos-lbc/projetos-elegiveis.api'
import { schemaTitulo, anosBase, origemCertificado } from '../../titulos-lbc.helpers'

const anoAtual = new Date().getFullYear()
const anos = anosBase()
const fmt = (n, ehPt) => Number(n || 0).toLocaleString(ehPt ? 'pt-BR' : 'en-US', { maximumFractionDigits: 2 })

const Secao = ({ numero, titulo }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 10, margin: '6px 0 12px' }}>
    <span style={{
      width: 22, height: 22, borderRadius: 6, background: '#EAF7FC', color: '#31ADE3',
      display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 600,
    }}
    >
      {numero}
    </span>
    <Typography variant="subtitle1" style={{ fontWeight: 600 }}>{titulo}</Typography>
  </div>
)

const Form = ({
  aberto, titulo, onFechar, onSalvo, onErro,
}) => {
  const { t, i18n } = useTranslation()
  const editando = !!titulo
  const ehPt = i18n.language === 'pt-BR'

  const [empresas, setEmpresas] = useState([])
  const [projetos, setProjetos] = useState([])
  const [saldo, setSaldo] = useState(null)
  const [certificado, setCertificado] = useState(null)
  const [salvando, setSalvando] = useState(false)
  const [erros, setErros] = useState({})
  const [uploadKey, setUploadKey] = useState(0)
  const [form, setForm] = useState({
    empresaId: titulo?.empresaGeradoraId || '',
    projetoId: titulo?.projetoId || '',
    certificadoCodigo: '',
    codigo: titulo?.codigo || '',
    anoBase: titulo?.anoBase || String(anoAtual),
    quantidade: titulo ? Number(titulo.quantidadeTotal) : '',
    anexos: [],
  })

  const projetoSel = useMemo(
    () => projetos.find((p) => p.id === Number(form.projetoId)),
    [projetos, form.projetoId],
  )

  // empresas (origem + destinatária)
  useEffect(() => {
    getEmpresas().then(setEmpresas).catch((e) => onErro(e.message))
  }, [onErro])

  // projetos elegíveis da empresa/ano escolhidos (na edição, usa o projeto do próprio título)
  useEffect(() => {
    if (editando) {
      setProjetos(titulo.projeto
        ? [{
          id: titulo.projetoId, nome: titulo.projeto.nome, ecorregiao: titulo.ecorregiao?.nome, lbc: 0, lbcDisponivel: 0,
        }]
        : [])
      return
    }
    if (!form.empresaId || !form.anoBase) { setProjetos([]); return }
    getProjetosElegiveis({ empresaId: form.empresaId, ano: form.anoBase })
      .then(setProjetos)
      .catch((e) => onErro(e.message))
  }, [editando, titulo, form.empresaId, form.anoBase, onErro])

  // saldo do certificado para o projeto/ano selecionado
  useEffect(() => {
    if (!form.projetoId || !form.anoBase) { setSaldo(null); return }
    getSaldoCertificado({ projetoId: form.projetoId, anoBase: form.anoBase })
      .then((r) => setSaldo(r.saldoDisponivel))
      .catch(() => setSaldo(null))
  }, [form.projetoId, form.anoBase])

  // Certificado já emitido para o projeto/ano: é dele que o título herda o
  // código-base e a ecorregião, então o código digitado passa a ser o dele.
  useEffect(() => {
    if (editando || !form.projetoId || !form.anoBase) { setCertificado(null); return undefined }
    // `vigente` descarta resposta atrasada: trocando projeto/ano rápido, a
    // resposta antiga chegaria depois e preencheria o código do outro projeto.
    let vigente = true
    getCertificadoAtivo({ projetoId: form.projetoId, anoBase: form.anoBase })
      .then((cert) => {
        if (!vigente) return
        setCertificado(cert)
        // Sem certificado, o campo volta a ficar vazio e editável — senão o
        // código do projeto anterior continuaria ali, agora liberado.
        setForm((f) => ({ ...f, certificadoCodigo: cert?.codigo || '' }))
      })
      .catch(() => { if (vigente) setCertificado(null) })
    return () => { vigente = false }
  }, [editando, form.projetoId, form.anoBase])

  const origem = useMemo(
    () => origemCertificado({ certificado, ecorregiaoProjeto: projetoSel?.ecorregiao }),
    [certificado, projetoSel],
  )

  const set = (campo) => (e) => setForm((f) => ({ ...f, [campo]: e.target.value }))
  const setEmpresaOrigem = (e) => setForm((f) => ({ ...f, empresaId: e.target.value, projetoId: '' }))
  const setAno = (e) => setForm((f) => ({ ...f, anoBase: e.target.value, projetoId: editando ? f.projetoId : '' }))

  const addAnexo = (anexo) => {
    if (!anexo) return
    setForm((f) => ({ ...f, anexos: [...f.anexos, anexo] }))
    setUploadKey((k) => k + 1)
  }
  const removeAnexo = (idx) => setForm((f) => ({ ...f, anexos: f.anexos.filter((_, i) => i !== idx) }))

  const submeter = async () => {
    setErros({})
    try {
      await schemaTitulo.validate(form, { abortEarly: false })
    } catch (validation) {
      const mapa = {}
      ;(validation.inner || []).forEach((er) => { mapa[er.path] = true })
      setErros(mapa)
      return
    }
    if (!editando && saldo != null && Number(form.quantidade) > Number(saldo)) {
      setErros({ quantidade: true })
      onErro(t('titulosLbc.saldoInsuficiente'))
      return
    }
    setSalvando(true)
    try {
      if (editando) {
        await editarTitulo(titulo.id, {
          anoBase: form.anoBase,
          quantidadeTotal: Number(form.quantidade),
        })
      } else {
        await criarTitulo({
          projetoId: Number(form.projetoId),
          anoBase: form.anoBase,
          certificadoCodigo: form.certificadoCodigo,
          codigo: form.codigo,
          quantidade: Number(form.quantidade),
          anexos: form.anexos.map((a) => ({ nomeAnexo: a.nomeAnexo, anexo: a.anexo })),
        })
      }
      onSalvo()
    } catch (e) {
      onErro(e.message)
    } finally {
      setSalvando(false)
    }
  }

  return (
    <Dialog open={aberto} onClose={onFechar} maxWidth="md" fullWidth>
      <DialogTitle>{editando ? t('titulosLbc.editar') : t('titulosLbc.novo')}</DialogTitle>
      <DialogContent dividers>

        <Secao numero={1} titulo={t('titulosLbc.secaoOrigem')} />
        <Typography
          variant="caption"
          style={{ display: 'block', marginBottom: 12, color: origem.divergente ? '#EE7D40' : undefined }}
          color={origem.divergente ? 'inherit' : 'textSecondary'}
        >
          {origem.temCertificado
            ? t('titulosLbc.origemAvisoCertificado', {
              codigo: origem.codigo,
              data: certificado?.createdAt ? new Date(certificado.createdAt).toLocaleDateString(i18n.language) : '',
            })
            : t('titulosLbc.origemAviso')}
        </Typography>
        {origem.divergente && (
          <Typography variant="caption" style={{ display: 'block', marginBottom: 12, color: '#EE7D40' }}>
            {t('titulosLbc.ecorregiaoDivergente', {
              certificado: origem.ecorregiao, projeto: projetoSel?.ecorregiao,
            })}
          </Typography>
        )}
        <Grid container spacing={2}>
          {/* Linha 1: Empresa Geradora | Ano Base */}
          <Grid item xs={12} sm={6}>
            <TextField
              select fullWidth label={t('titulosLbc.geradora')} value={form.empresaId}
              onChange={setEmpresaOrigem} disabled={editando} error={!!erros.empresaId}
            >
              {empresas.map((em) => <MenuItem key={em.id} value={em.id}>{em.nome}</MenuItem>)}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              select fullWidth label={t('titulosLbc.anoBase')} value={form.anoBase}
              onChange={setAno} error={!!erros.anoBase}
            >
              {anos.map((a) => <MenuItem key={a.value} value={a.value}>{a.label}</MenuItem>)}
            </TextField>
          </Grid>
          {/* Linha 2: Projeto | Ecorregião */}
          <Grid item xs={12} sm={6}>
            <TextField
              select fullWidth label={t('titulosLbc.projeto')} value={form.projetoId}
              onChange={set('projetoId')} disabled={editando || !form.empresaId} error={!!erros.projetoId}
              helperText={!editando && form.empresaId && projetos.length === 0 ? t('titulosLbc.semProjetosElegiveis') : ''}
            >
              {projetos.map((p) => <MenuItem key={p.id} value={p.id}>{p.nome}</MenuItem>)}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth label={t('titulosLbc.ecorregiao')} value={origem.ecorregiao || ''}
              disabled InputProps={{ startAdornment: <InputAdornment position="start"><Lock fontSize="small" /></InputAdornment> }}
            />
          </Grid>
          {projetoSel && (
            <Grid item xs={12}>
              <Chip
                label={`${fmt(projetoSel.lbcDisponivel, ehPt)} LBC · ${t('titulosLbc.pontuacaoExcedente')}`}
                style={{ background: '#F3FAF6', color: '#219754' }}
              />
            </Grid>
          )}
        </Grid>

        <Divider style={{ margin: '20px 0' }} />

        <Secao numero={2} titulo={t('titulosLbc.secaoDados')} />
        <Grid container spacing={2}>
          {!editando && (
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth label={t('titulosLbc.codigoCertificado')} value={form.certificadoCodigo}
                onChange={set('certificadoCodigo')} disabled={origem.temCertificado}
                InputProps={origem.temCertificado
                  ? { startAdornment: <InputAdornment position="start"><Lock fontSize="small" /></InputAdornment> }
                  : undefined}
                helperText={origem.temCertificado ? t('titulosLbc.codigoCertificadoExistente') : 'LIFE.LBC.00X.BRA.00XX.20XX'}
              />
            </Grid>
          )}
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth label={t('titulosLbc.codigo')} value={form.codigo}
              onChange={set('codigo')} disabled={editando} error={!!erros.codigo}
              helperText={editando ? t('titulosLbc.codigoImutavel') : 'LIFE.LBC.BRA.00XX.20XX.GEN.TX.202X'}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <NumericFormat
              customInput={TextField}
              fullWidth
              label={t('titulosLbc.quantidade')}
              value={form.quantidade}
              onValueChange={(vals) => setForm((f) => ({ ...f, quantidade: vals.floatValue ?? '' }))}
              thousandSeparator={ehPt ? '.' : ','}
              decimalSeparator={ehPt ? ',' : '.'}
              decimalScale={2}
              allowNegative={false}
              error={!!erros.quantidade}
              helperText={saldo != null ? `${t('titulosLbc.disponivel')}: ${fmt(saldo, ehPt)}` : ''}
            />
          </Grid>
        </Grid>

        {!editando && (
          <>
            <Divider style={{ margin: '20px 0' }} />
            <Secao numero={3} titulo={t('titulosLbc.anexos')} />
            {form.anexos.map((a, idx) => (
              <Chip
                key={`${a.nomeAnexo}-${idx}`}
                label={a.nomeAnexo}
                onDelete={() => removeAnexo(idx)}
                style={{ marginRight: 8, marginBottom: 8 }}
              />
            ))}
            <UploadFile key={uploadKey} onChange={addAnexo} title={t('titulosLbc.adicionarAnexo')} />
          </>
        )}

      </DialogContent>
      <DialogActions>
        <Button onClick={onFechar} disabled={salvando}>{t('inputs.cancelar')}</Button>
        <Button onClick={submeter} color="primary" variant="contained" disabled={salvando}>
          {t('inputs.salvar')}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default Form
